import { Injectable } from '@angular/core';
import { Response } from '@angular/http';
import { MdDialog, MdDialogRef } from '@angular/material';
import { Router } from '@angular/router';
import { DialogAlertComponent } from 'app/components/modal/dialog-alert/dialog-alert.component';
import { BudgetAvailableRequest, BudgetAvailableResponse } from 'app/model/cart/budget-available.model';
import { SearchFormModel } from 'app/model/search-form-model';
import { TranslatePipe } from 'app/pipes/translate.pipe';
import { AppConfigService } from 'app/services/app-config/app-config.service';
import { UserService } from 'app/services/user/user.service';
import { BehaviorSubject } from 'rxjs/BehaviorSubject';
import { Observable } from 'rxjs/Observable';
import { Subject } from 'rxjs/Subject';
import * as moment from 'vendors/moment-locale-th';
import { environment } from '../../../environments/environment';
import { AvailableBudget, CartModel, CartMultipaymentDetail, CreditTermsAmount } from '../../model/cart.model';
import { CartInfoModel } from '../../model/checkout/cart-info-model';
import { FeatureType } from '../../model/constant';
import { RoomConditionDto } from '../../model/products/product-room-detail.model';
import { BudgetLimitModel } from '../../model/user.model';
import { ActHttpService } from '../act-http/act-http.service';
import { AuthorizeService } from '../authorize/authorize.service';
import { ProductService } from '../products/product.service';
import { PurposeType } from '../search/search.service';
import { PurposeType as PurposeTypeString } from 'app/model/constant';
import { AddCartPayload } from './payload/add-cart-payload';
import { ValidatePromotionCodeResponse } from '../promotion-code/model/promotion-code.model';

@Injectable()
export class CartService {

  private cartProduct$ = new Subject<CartInfoModel>();
  private cartMultiPaymentDetail$ = new BehaviorSubject<CartMultipaymentDetail>(null);
  private cartMessagel$ = new BehaviorSubject<any>(null);
  private cartLoading$ = new BehaviorSubject<any>(null);
  private cartBudgetAvailable$ = new BehaviorSubject<BudgetAvailableResponse>(null);
  private cartCancelShareBudget$ = new BehaviorSubject<boolean>(null);
  private cartMultiPaymentMainChannel$ = new BehaviorSubject<string>(null);

  public currentCartMultiPaymentDetail = this.cartMultiPaymentDetail$.asObservable();

  constructor(
    private actHttp: ActHttpService,
    private appConfigService: AppConfigService,
    private productService: ProductService,
    private userService: UserService,
    private translate: TranslatePipe,
    private authorizeService: AuthorizeService,
    private dialog: MdDialog,
    private router: Router
  ) { }

  addCartV4(data: AddCartPayload): Observable<any> {
    return this.actHttp.post(
      environment.endpoint.addToCartV4,
      data,
      this.actHttp.getRequestOptions()
    ).map((res: Response) => res.json());
  }

  getCartV4CheckOut(cartProductId: String): Observable<any> {
    const url = `${environment.endpoint.getCartV4Checkout}`.replace('{cartId}', cartProductId.toString());
    return this.actHttp.get(
      url,
      this.actHttp.getRequestOptions()
    ).map((res: Response) => res.json());
  }

  updateTempCart(cartData: CartInfoModel) {
    this.cartProduct$.next(cartData);
  }

  getTempCart$(): Observable<CartInfoModel> {
    return this.cartProduct$.asObservable();
  }

  calculatePayOnDate(roomConditions: RoomConditionDto[]): string {
    if (roomConditions && roomConditions.length === 0) {
      return '';
    }
    const formatDate = this.appConfigService.dateAPIFormat;
    const isPaynow = roomConditions.filter(item => !item.payOn || item.payOn === '').length;
    return isPaynow ? moment().format(formatDate)
      : roomConditions.sort(this.sortPayLaterDate.bind(this))[0].payOn;
  }

  calculateTotalPrice(conditions: RoomConditionDto[], night: number = 1): void {
    conditions.forEach((item) => {
      item.totalPrice = '' + (item.netPrice ? +item.netPrice : 0) * night;
    });
  }

  calculateGrandTotalPrice(conditions: RoomConditionDto[], validatePromoCode: ValidatePromotionCodeResponse): number {
    let totalPrice = 0.00;
    totalPrice = conditions.reduce((acc, cur) => acc + (
      (cur.totalPrice ? +cur.totalPrice : 0) * (cur.quantity ? +cur.quantity : 0)
    ), totalPrice);
    if (validatePromoCode && validatePromoCode.valid) {
      totalPrice = validatePromoCode.finalAmount;
    }
    return totalPrice;
  }

  calculateTotalRoomSelected(conditions: RoomConditionDto[]) {
    let totalRoom = 0;
    totalRoom = conditions.reduce((acc, cur) => acc + (
      cur.quantity ? +cur.quantity : 0
    ), 0);
    return totalRoom;
  }

  calculateBookNight(checkInDate, checkOutDate) {
    if (checkInDate === undefined || checkOutDate === undefined) {
      return 1;
    }
    const dateForm = moment(checkInDate, this.appConfigService.dateHumanFormat);
    const dateTo = moment(checkOutDate, this.appConfigService.dateHumanFormat);
    // console.log(`dateFrom: ${dateForm} , dateTo: ${dateTo}`);
    return dateTo.diff(dateForm, 'days');
  }

  /**
   * Check room limit on cart
   * @param {RoomConditionDto[]} conditions
   * @param {number} [roomLimit=1]
   * @returns boolean
   * @desc Return true = avaiable to book, false = not allow
   * @memberof CartService
   */
  checkBookingRoomLimit(conditions: RoomConditionDto[], roomLimit: number = 1) {
    const totalRoom = this.calculateTotalRoomSelected(conditions);
    return totalRoom > 0 && totalRoom <= roomLimit;
  }

  private sortPayLaterDate(first: RoomConditionDto, second: RoomConditionDto) {
    const formatDate = this.appConfigService.dateAPIFormat;
    const firstDate = moment(first.payOn, formatDate);
    const secondDate = moment(second.payOn, formatDate);
    const diffDate = firstDate.diff(secondDate, 'days');
    if (diffDate < 0) {
      return -1;
    }
    if (diffDate > 0) {
      return 1;
    }
    return 0;
  }

  verifyAddToCart(cart: CartModel, searchData: SearchFormModel, showNotifyDialog: Function) {
    if (!cart || cart.conditions.length === 0) {
      showNotifyDialog('cart-select-room');
      return false;
    }
    let roomLimit = 9;
    if (searchData.purpose === PurposeType[PurposeType.work]) {
      const limitRoomFeatures = this.userService.getUser().features
        .find(feature => (feature.feature === FeatureType.ALLOW_LIMIT_ROOM.toUpperCase()
          && feature.allow));
      if (limitRoomFeatures) {
        roomLimit = limitRoomFeatures.data ? limitRoomFeatures.data : 1;
      }
    }
    const isOverBookingRoomLimit = !this.checkBookingRoomLimit(cart.conditions, roomLimit);
    if (isOverBookingRoomLimit) {
      const msg = this.appConfigService.getTranslate('condition-limit-room-title').replace('${var}', '' + roomLimit);
      showNotifyDialog(undefined, undefined, msg);
      return false;
    }
    return true;
  }

  convertPrice(cart: CartModel): RoomConditionDto[] {
    const night = this.calculateBookNight(cart.search.checkInDate, cart.search.checkOutDate);
    return cart.conditions.map((roomCondition, roomIndex) => {
      if (cart.optionDetail && cart.optionDetail.optionList && cart.optionDetail.optionList.length > 0) {
        cart.optionDetail.optionList.map((option, optionIndex) =>
          option.conditionList.map(condition => {
            if (roomIndex === optionIndex) {
              roomCondition.netPrice = (condition.totalPriceSellingFinalSupplierCurrency.valueOf() / night).toString();
            }
          })
        );
      }
      return roomCondition;
    });
  }

  getUserBudgetInfo(searchData: SearchFormModel): BudgetLimitModel {
    return this.userService.checkBudgetLimit(searchData);
  }

  updateCartMultiPaymentDetail(detail: CartMultipaymentDetail) {
    if (JSON.stringify(this.cartMultiPaymentDetail$.value) !== JSON.stringify(detail)) {
      this.cartMultiPaymentDetail$.next(detail);
    }
  }

  getCartMultiPaymentDetail(): Observable<CartMultipaymentDetail> {
    return this.cartMultiPaymentDetail$.asObservable();
  }

  calculateTotalAdult(conditions: RoomConditionDto[]) {
    let totalAdult = 0;
    totalAdult = conditions.reduce((acc, cur) => acc + (
      (cur.adult ? +cur.adult : 0) * (cur.quantity ? +cur.quantity : 0)
    ), totalAdult);
    return totalAdult;
  }

  calculateTotalChild(conditions: RoomConditionDto[]) {
    let totalChild = 0;
    totalChild = conditions.reduce((acc, cur) => acc + (
      (cur.child ? +cur.child : 0) * (cur.quantity ? +cur.quantity : 0)
    ), totalChild);
    return totalChild;
  }

  getBudgetAvailable(request: BudgetAvailableRequest): Observable<any> {
    return this.actHttp.post(
      environment.endpoint.budgetAvailable,
      request,
      this.actHttp.getRequestOptions()
    ).map((res: Response) => res.json());
  }

  updateCartMessage(message) {
    this.cartMessagel$.next(message);
  }

  getCartMessage(): Observable<any> {
    return this.cartMessagel$.asObservable();
  }

  updateCartLoading(loading) {
    this.cartLoading$.next(loading);
  }

  getCartLoading(): Observable<any> {
    return this.cartLoading$.asObservable();
  }

  getMultiPaymentMainChannel() {
    return this.cartMultiPaymentMainChannel$.asObservable();
  }

  updateMultiPaymentMainChannel(data) {
    this.cartMultiPaymentMainChannel$.next(data);
  }

  getCartBudgetAvailable() {
    return this.cartBudgetAvailable$.asObservable();
  }

  updateCartBudgetAvailable(data) {
    this.cartBudgetAvailable$.next(data);
  }

  messageBudgetAvailable(request: BudgetAvailableRequest, budgetPerNight: number, night: number) {
    this.updateMultiPaymentMainChannel('');
    const allowMultiPayment = this.productService.allowMultiPayment(this.userService.getUser());
    const allowMultiPaymentCreditTerms = this.productService.allowMultiPayment(this.userService.getUser(), true);
    const allowMultiPaymentCorporateFleet = this.productService.allowMultiPayment(this.userService.getUser(), false, true);

    const totalBudget = budgetPerNight * night;
    this.updateCartLoading({ loading: true, error: false });
    this.getBudgetAvailable(request).subscribe((res) => {
      if (res && res.data) {
        const data: BudgetAvailableResponse = res.data;
        this.updateCartBudgetAvailable(data);

        const multipaymentCreditterms = allowMultiPaymentCreditTerms && data.totalAvailableBudget > 0 && request.totalSellingPrice > data.totalAvailableBudget;
        const multiPaymentCorporateFleet = allowMultiPaymentCorporateFleet && totalBudget && totalBudget > 0 && request.totalSellingPrice > totalBudget;

        if (request.shareBudgetCode) {
          const half = this.calculateHalfBudget(data.totalAvailableBudget, data.totalAvailableBudgetBooker, data.totalAvailableBudgetCoBooker);
          const detail = new CartMultipaymentDetail(
            data.totalOverBudget,
            half.booker,
            half.coBooker
          );
          detail.availableBudget = new AvailableBudget(data.totalAvailableBudgetBooker, data.totalAvailableBudgetCoBooker, data.totalAvailableBudget);
          detail.isLimitBudgetPerMonthPerYearBooker = data.budgetPerMonth.length > 0 || data.budgetPerYear.length > 0;
          detail.isLimitBudgetPerMonthPerYearCoBooker = data.budgetPerMonthCoBooker.length > 0 || data.budgetPerYearCoBooker.length > 0;

          this.updateCartMultiPaymentDetail(detail);
        } else if (multipaymentCreditterms) {
          const cartMultipaymentDetail = new CartMultipaymentDetail(data.totalOverBudget, data.totalAvailableBudget);
          this.updateCartMultiPaymentDetail(cartMultipaymentDetail);
        } else if (multiPaymentCorporateFleet) {
          const cartMultipaymentDetail = new CartMultipaymentDetail(request.totalSellingPrice - totalBudget, totalBudget);
          this.updateCartMultiPaymentDetail(cartMultipaymentDetail);
          this.updateMultiPaymentMainChannel('corporate-fleet');
        } else {
          this.updateCartMultiPaymentDetail(null);
        }
      }

      if (res.errors && res.errors.length > 0 && res.errors[0].code) {
        switch (res.errors[0].code) {
          case '2004':
            if (request.shareBudgetCode) {
              let text = res.errors[0].message;
              this.updateCartMessage({ text: text.charAt(0).toUpperCase() + text.slice(1), code: res.errors[0].code, event: 'cannot' });
            } else {
              let text = this.translate.transform('cart-budget-no-remaning');
              let subtext = this.appConfigService.getLanguage() === 'th' ? 'เนื่องจาก' + res.errors[0].message : res.errors[0].message + '.';
              if (!this.userService.hideMessageCart(this.userService.getUser())) {
                this.updateCartMessage({ text: text.charAt(0).toUpperCase() + text.slice(1), subText: subtext, code: res.errors[0].code });
              }
            }
            break;
          case '2005':
            if (request.shareBudgetCode) {
              let text = res.errors[0].message;
              this.updateCartMessage({ text: text.charAt(0).toUpperCase() + text.slice(1), code: res.errors[0].code, event: 'cannot', continueType: res.data.totalOverBudget === 0 ? 'myBudget' : 'multipayment' });
            } else {
              if (allowMultiPaymentCreditTerms) {
                let text = res.errors[0].message;
                this.updateCartMessage({ text: text.charAt(0).toUpperCase() + text.slice(1), subText: this.translate.transform('cart-budget-detail-below'), code: res.errors[0].code });
              } else {
                let text = this.translate.transform('cart-budget-no-remaning');
                let subtext = this.appConfigService.getLanguage() === 'th' ? 'เนื่องจาก' + res.errors[0].message : res.errors[0].message + '.';
                if (!this.userService.hideMessageCart(this.userService.getUser())) {
                  this.updateCartMessage({ text: text.charAt(0).toUpperCase() + text.slice(1), subText: subtext, code: res.errors[0].code });
                }
              }
            }
            break;
          case '2006':
            if (request.shareBudgetCode) {
              if (allowMultiPayment) {
                let text = res.errors[0].message;
                this.updateCartMessage({ text: text.charAt(0).toUpperCase() + text.slice(1), subText: this.translate.transform('cart-share-budget-detail-below'), code: res.errors[0].code });
              } else {
                let text = res.errors[0].message;
                this.updateCartMessage({ text: text.charAt(0).toUpperCase() + text.slice(1), code: res.errors[0].code, event: 'cannot' });
              }
            }
            break;
        }
      }
      this.updateCartLoading({ loading: false, error: false });
    }, (err) => {
      if (err && err.json() && err.json().errors && err.json().errors.length > 0) {
        const errors = err.json().errors;
        let alertText;
        let systemError: boolean
        switch (errors[0].code) {
          case '4701':
            alertText = `<strong>${this.translate.transform('dialog-budget-cannot-share-budget-co-booker')}</strong><br>${this.translate.transform('search-box-error-code-ref-incorrect')}`;
            break;
          case '4702':
            alertText = `<strong>${this.translate.transform('dialog-budget-cannot-share-budget-co-booker')}</strong><br>${this.translate.transform('search-box-error-code-ref-expired')}`;
            break;
          case '4704':
          case '4705':
            alertText = `<strong>${this.translate.transform('dialog-budget-cannot-share-budget-co-booker')}</strong><br>${this.translate.transform('search-box-error-feature-not-allow')}`;
            break;
          default:
            systemError = true;
        }

        if (!systemError) {
          const dialogRef: MdDialogRef<DialogAlertComponent> = this.dialog.open(DialogAlertComponent);
          dialogRef.componentInstance.alertText = alertText;
          dialogRef.componentInstance.imageSrc = '/assets/images/icons/icon-failed.png';
          dialogRef.componentInstance.imageSrcSet = '/assets/images/icons/icon-failed.png';
          dialogRef.componentInstance.confirmText = this.translate.transform('search-box-cancel-share-budget');

          return dialogRef.afterClosed().subscribe(m => {
            this.authorizeService.removeCoBookerCookie();
            if (errors[0].code === '4704') {
              this.authorizeService.setCookieFeatureShareBudgetIsFalse();
            }
            // Reload current router
            this.router.routeReuseStrategy.shouldReuseRoute = () => false;
            this.router.navigated = false;
            this.router.navigateByUrl(this.router.url);
            return Observable.of(false);
          });
        }
        this.updateCartLoading({ loading: false, error: true });
      }
    });
  }

  calculateHalfBudget(availableBudget: number, bookerBudget: number, coBookerBudget: number) {
    let result = { booker: 0, coBooker: 0 };
    const halfRoundUp = Math.ceil(availableBudget / 2);

    if (coBookerBudget < bookerBudget) {
      let val = this.compareBudget(halfRoundUp, coBookerBudget);
      result.coBooker = val.value;
      result.booker = availableBudget - halfRoundUp + val.carry;

      if (Math.abs(result.coBooker - result.booker) === 1) {
        result.coBooker = result.coBooker - 1;
        result.booker = result.booker + 1;
      }
    } else {
      let val = this.compareBudget(halfRoundUp, bookerBudget);
      result.booker = val.value;
      result.coBooker = availableBudget - halfRoundUp + val.carry;
    }

    return result;
  }

  compareBudget(halfRoundUp: number, budget: number) {
    let value;
    let carry;

    if (halfRoundUp > budget) {
      value = budget;
      carry = halfRoundUp - budget;
    } else {
      value = halfRoundUp;
      carry = 0;
    }

    return { value: value, carry: carry };
  }

  updateCartCancelShareBudget() {
    this.cartCancelShareBudget$.next(true);
  }

  getCartCancelShareBudget(): Observable<boolean> {
    return this.cartCancelShareBudget$.asObservable();
  }

  verifyShouldCheckBudgetAvailable(purpose): boolean {
    const isAllowCreditTerms = this.userService.getUserFeature(FeatureType.ALLOW_CREDIT_TERMS).allow;
    const isAllowBookForTraveller = this.userService.getUserFeature(FeatureType.ALLOW_BOOK_FOR_TRAVELLER) && this.userService.getUserFeature(FeatureType.ALLOW_BOOK_FOR_TRAVELLER).allow;
    return isAllowCreditTerms && !isAllowBookForTraveller
      && purpose === PurposeTypeString.work
      && (this.isBudgetAvailable() || this.authorizeService.getShareBudgetCodeCookie());
  }

  isBudgetAvailable() {
    const limitBudget = this.userService.getUserFeature(FeatureType.ALLOW_LIMIT_BUDGET);
    return limitBudget && limitBudget.data && (limitBudget.data.amountPerMonth || limitBudget.data.amountPerYear);
  }
}



// WEBPACK FOOTER //
// ./src/app/services/cart/cart.service.ts